// Photos and files in a chat message (TOM-671): what the chat box accepts,
// in what number, and the words it says when it cannot take one.
//
// The server reads each file's type from its bytes (api_contracts §12.17, ADR
// 0269): JPEG, PNG and WebP photos and PDFs, at most 5 MB each, at most four
// with one message (§12.2). The chat box checks the same rules first, from the
// file's declared type and size, so the owner hears about a file it cannot
// send before it is uploaded. The server stays the judge: a file that passes
// here can still be refused, and its words are shown then.
//
// Pure, so cloud/web's Node vitest covers it. The photo shrink, which needs a
// canvas, is in prepareChatPhoto below and runs only in the browser.

/** At most this many files go with one message (§12.2). */
export const CHAT_ATTACHMENTS_MAX = 4;

/** At most this many bytes in one file (§12.17). */
export const CHAT_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;

/**
 * The longest side a photo is sent at. The provider shrinks a photo past it
 * anyway (§12.17), so a bigger one is shrunk here and loses nothing.
 */
export const CHAT_PHOTO_MAX_EDGE = 2576;

/** The types the picker offers, and the ones a paste or a drop may carry. */
export const CHAT_ATTACHMENT_ACCEPT =
  'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf';

export type ChatAttachmentKind = 'image' | 'file';

/** What a file is to the chat box, from its declared type and its name. */
export function chatAttachmentKind(file: {
  type: string;
  name: string;
}): ChatAttachmentKind | null {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  if (type === 'application/pdf' || (type === '' && name.endsWith('.pdf'))) return 'file';
  if (type === 'image/jpeg' || type === 'image/png' || type === 'image/webp') return 'image';
  // HEIC is not accepted by the server; the browser turns it into a JPEG
  // first where it can read one (prepareChatPhoto), as the app does.
  if (type === 'image/heic' || type === 'image/heif') return 'image';
  if (type === '' && /\.(jpe?g|png|webp|heic|heif)$/.test(name)) return 'image';
  return null;
}

/** One file the chat box was handed, as far as these rules care. */
export interface ChatFileLike {
  type: string;
  name: string;
  size: number;
}

/** Each file taken, and a sentence for each one turned away. */
export interface ChatAttachmentPick<F extends ChatFileLike> {
  accepted: F[];
  refused: string[];
}

/**
 * The files from `files` the message can take, given `already` attached, in
 * the order they came, and why any was left out. Too big a photo is taken:
 * it is shrunk before it is sent. Too big a PDF is not.
 */
export function pickChatAttachments<F extends ChatFileLike>(
  files: readonly F[],
  already: number
): ChatAttachmentPick<F> {
  const accepted: F[] = [];
  const refused: string[] = [];
  let full = false;
  for (const file of files) {
    const kind = chatAttachmentKind(file);
    const label = file.name.length > 0 ? file.name : 'That file';
    if (kind === null) {
      refused.push(`${label} can’t be sent. Neo reads photos (JPEG, PNG, WebP) and PDFs.`);
      continue;
    }
    if (kind === 'file' && file.size > CHAT_ATTACHMENT_MAX_BYTES) {
      refused.push(`${label} is bigger than 5 MB.`);
      continue;
    }
    if (already + accepted.length >= CHAT_ATTACHMENTS_MAX) {
      full = true;
      continue;
    }
    accepted.push(file);
  }
  if (full) refused.push(`A message can carry ${CHAT_ATTACHMENTS_MAX} photos or files.`);
  return { accepted, refused };
}

/**
 * The files a paste or a drop carries. A copied photo, a screenshot or a file
 * copied in the Finder arrives as a file; text arrives as text and is left to
 * the text field. Safari lists a pasted file twice (once in `files`, once in
 * `items`), so `files` wins when it holds any.
 */
export function filesFromTransfer(
  transfer: {
    files?: ArrayLike<File> | null;
    items?: ArrayLike<{ kind: string; getAsFile(): File | null }> | null;
  } | null
): File[] {
  if (transfer === null) return [];
  const files = Array.from(transfer.files ?? []);
  if (files.length > 0) return files;
  const out: File[] = [];
  for (const item of Array.from(transfer.items ?? [])) {
    if (item.kind !== 'file') continue;
    const file = item.getAsFile();
    if (file !== null) out.push(file);
  }
  return out;
}

/** The server's answer to an upload (§12.17 `201`). */
export interface UploadedChatAttachment {
  attachmentId: string;
  kind: ChatAttachmentKind;
  mime: string;
  bytes: number;
  width?: number;
  height?: number;
  pages?: number;
  filename?: string;
}

/** The §12.17 body, or null when it is not one. */
export function parseUploadedChatAttachment(body: unknown): UploadedChatAttachment | null {
  if (typeof body !== 'object' || body === null) return null;
  const b = body as Record<string, unknown>;
  if (typeof b.attachmentId !== 'string' || b.attachmentId.length === 0) return null;
  if (b.kind !== 'image' && b.kind !== 'file') return null;
  if (typeof b.mime !== 'string' || typeof b.bytes !== 'number') return null;
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
  const width = num(b.width);
  const height = num(b.height);
  const pages = num(b.pages);
  return {
    attachmentId: b.attachmentId,
    kind: b.kind,
    mime: b.mime,
    bytes: b.bytes,
    ...(width === undefined ? {} : { width }),
    ...(height === undefined ? {} : { height }),
    ...(pages === undefined ? {} : { pages }),
    ...(typeof b.filename === 'string' && b.filename.length > 0 ? { filename: b.filename } : {}),
  };
}

/** An owner message's attachments as the conversation read returns them (§12.11). */
export function parseChatAttachments(value: unknown): UploadedChatAttachment[] {
  if (!Array.isArray(value)) return [];
  const out: UploadedChatAttachment[] = [];
  for (const raw of value) {
    const parsed = parseUploadedChatAttachment(raw);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

/** Where an attachment's bytes are read, with the owner's own session (§12.18). */
export function chatAttachmentSrc(attachmentId: string): string {
  return `/v1/neo/attachments/${encodeURIComponent(attachmentId)}`;
}

/** What the owner is told when an upload is refused, by the server's status. */
export function chatUploadErrorMessage(
  status: number,
  body: { message?: unknown } | null,
  name: string
): string {
  const label = name.length > 0 ? name : 'That file';
  if (status === 413) {
    return typeof body?.message === 'string' && body.message.length > 0
      ? `${label}: ${body.message}`
      : `${label} is too big to send.`;
  }
  if (status === 415) return `${label} can’t be sent. Neo reads photos (JPEG, PNG, WebP) and PDFs.`;
  if (status === 400) return `${label} couldn’t be read. A PDF with a password can’t be sent.`;
  if (status === 429) return 'Too many uploads for now. Try again in a little while.';
  if (status === 401) return 'You’ve been signed out. Sign in again to send a file.';
  return `${label} didn’t upload. Try again.`;
}

/** How big a photo is drawn to fit `maxEdge` on its longer side. */
export function fitWithin(width: number, height: number, maxEdge: number) {
  const longer = Math.max(width, height);
  if (longer <= maxEdge || longer === 0) return { width, height };
  const scale = maxEdge / longer;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** A file's size in words: "1.2 MB", "840 KB". */
export function fileSizeLabel(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * A photo as it should be uploaded. A JPEG or HEIC photo is drawn again as a
 * JPEG, which drops its location data (§12.17 asks a client to strip EXIF
 * GPS) and turns a HEIC into what the server reads. Any photo past the
 * provider's long edge or the 5 MB cap is shrunk the same way. A PNG or WebP
 * within the limits, a screenshot most often, goes as it is, so its text
 * stays sharp, unless `redraw` asks for every photo to be drawn again (a
 * boat's home photo, TOM-683, which every owner sees). A photo the browser
 * cannot draw goes as it is, and the server's answer decides.
 */
export async function prepareChatPhoto(file: File, opts: { redraw?: boolean } = {}): Promise<File> {
  const type = file.type.toLowerCase();
  const jpegLike =
    type === 'image/jpeg' || type === 'image/heic' || type === 'image/heif' || type === '';
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }
  try {
    const big =
      Math.max(bitmap.width, bitmap.height) > CHAT_PHOTO_MAX_EDGE ||
      file.size > CHAT_ATTACHMENT_MAX_BYTES;
    if (!jpegLike && !big && opts.redraw !== true) return file;
    const size = fitWithin(bitmap.width, bitmap.height, CHAT_PHOTO_MAX_EDGE);
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return file;
    // A transparent PNG drawn into a JPEG would turn black behind its shapes.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size.width, size.height);
    ctx.drawImage(bitmap, 0, 0, size.width, size.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.88)
    );
    if (blob === null) return file;
    const base = file.name.replace(/\.[^.]+$/, '');
    return new File([blob], `${base.length > 0 ? base : 'photo'}.jpg`, { type: 'image/jpeg' });
  } finally {
    bitmap.close();
  }
}
